/**
 * THE FOSSILS — every old admin page that a question replaces, and the rule
 * for when it dies.
 *
 * Design §3.1: "the URL of every old admin page redirects into the question
 * it served with its scope preserved. A test asserts that no admin route
 * other than the ten, the Tools door and the redirects renders." The first
 * slice noted the honest exception: redirecting a working page to a question
 * that is not built yet would be a worse surface, not a smaller one. So a
 * fossil renders WHILE any question it serves is unbuilt, and the moment the
 * last of them is built it becomes a redirect — decided here, by code, and
 * enforced by intel/grammar.test.ts, so building a question without killing
 * its fossil fails the build rather than adding a sixth way.
 *
 * `boards` on /admin/stats maps each old board to the question that took its
 * job, so a bookmarked board lands on its answer rather than on the first
 * question. Boards with no world-scope question — rate compare, voice and
 * pause, coverage — live on at node scope on the node insights page, which
 * is the org dashboard's and stays exactly as it is.
 */
import { QUESTIONS, questionPath, questionBySlug } from './questions'

/**
 * Whether the Discovery feed's findings are shown as cards on the question
 * they concern. The feed page dies when they are — see QuestionFindings.vue.
 */
export const FINDINGS_ON_QUESTIONS = true

export interface Fossil {
  /** Path under /admin, as the router writes it. */
  path: string
  /** The questions this page served. Empty when it is replaced by the findings cards. */
  questions: string[]
  /** Where a bookmark lands once the fossil is dead — defaults to the first question. */
  landing?: string
  /** For /admin/stats: old ?board= id → the question slug that took its job. */
  boards?: Record<string, string>
  /**
   * For /admin/stats: boards no question answers yet. While any is listed the
   * fossil stays alive even when every question it served is built, because
   * killing it would lose those boards outright (job #459).
   */
  unanswered?: string[]
  /** One line on what died and what took over, for the reader of this file. */
  note: string
}

export const FOSSILS: readonly Fossil[] = [
  {
    path: 'analytics',
    questions: ['learning'],
    note: 'Platform Analytics, five tabs of hand-rolled charts. Its Overview and Growth tabs asked the pulse, whose content moved to Learning on 2026-09-27.',
  },
  {
    path: 'board',
    questions: ['learning'],
    note: 'The living board report: real learners in thirty days, minutes, schools total. Learning carries that number with its population shown.',
  },
  {
    path: 'attention',
    questions: ['business'],
    note: 'Needs attention: subscribers with no practice in seven days or a period ending. The about-to-leave rows on Business, ranked by recency and regularity.',
  },
  {
    path: 'courses',
    questions: ['learning'],
    note: 'Course tiles with enrolment and practice stats. Learning compares every course against the average of all courses.',
  },
  {
    path: 'activity',
    questions: ['working'],
    note: 'The live timeline. Working now answers "is it working right now" with the failure rate by build and device.',
  },
  {
    path: 'users/:learnerId',
    questions: ['person'],
    landing: '/intel/person',
    note: 'The 2,100-line user detail page. One person keeps everything on it that answers a support call, in the five-part shape.',
  },
  {
    path: 'stats',
    questions: ['business', 'content', 'learning', 'working'],
    boards: {
      lifecycle: 'business',
      difficulty: 'learning',
      friction: 'content',
      scoreboard: 'learning',
      health: 'working',
    },
    unanswered: ['rates', 'vad', 'coverage'],
    note: 'Eight Insight Engine boards driven by hand. Each board maps to the question that took its job. Rate compare, voice and pause, and coverage have no question page, so the page keeps rendering for them until one does, and node insights links to it as All boards.',
  },
  {
    path: 'insights',
    questions: [],
    landing: '/intel/business',
    note: 'The Discovery feed as a page. The engine stays; the findings become cards at the top of the question each concerns. Its URL is the one Tom types for the intelligence surface, so it lands on the business numbers — the page the surface now opens on.',
  },
] as const

/**
 * The route name a fossil carries WHILE IT IS ALIVE, as the router writes it:
 * the path with every non-letter run collapsed to a hyphen. A dead fossil is a
 * bare redirect and has no name at all. This lives here rather than in the
 * router so the continuity pin can assert the absence of the name the code
 * really generates, rather than of a name nobody ever defined.
 */
export function fossilRouteName(f: Fossil): string {
  return `admin-${f.path.replace(/[^a-z]+/g, '-').replace(/-$/, '')}`
}

/** True once every question a fossil served is built and nothing it shows is left unanswered — the day it redirects. */
export function fossilIsDead(f: Fossil): boolean {
  if (f.unanswered?.length) return false
  if (f.questions.length === 0) return FINDINGS_ON_QUESTIONS
  return f.questions.every((slug) => questionBySlug(slug)?.built === true)
}

/** Where a dead fossil's URL lands, scope preserved where the old URL carried one. */
export function fossilLanding(
  f: Fossil,
  query: Record<string, unknown> = {},
  params: Record<string, unknown> = {},
): { path: string; query?: Record<string, string> } {
  const first = questionBySlug(f.questions[0] ?? 'business') ?? QUESTIONS[0]
  let path = f.landing ?? questionPath(first)
  const board = typeof query.board === 'string' ? query.board : null
  if (f.boards && board && f.boards[board]) {
    const q = questionBySlug(f.boards[board])
    if (q) path = questionPath(q)
  }
  const preserved: Record<string, string> = {}
  if (typeof query.course === 'string') preserved.course = query.course
  // The old user page carried the person in the path; the question carries
  // them in the query, like every other scope.
  if (typeof params.learnerId === 'string') preserved.person = params.learnerId
  return Object.keys(preserved).length ? { path, query: preserved } : { path }
}
